/**
 * Keep-alive page registry for the main content host.
 * One entry per pageKey in the active workspace.
 */
import {findWorkspaceCapableElements} from './workspaceStateUtil.js';

export class PageRegistry {
	constructor() {
		this.pages = new Map();
		this.activePageKey = null;
		this.currentWorkspaceName = 'Default';
		this.currentWorkspaceId = null;
		this.listeners = [];
		this.contentHost = null;
	}

	setContentHost(contentHost) {
		this.contentHost = contentHost;
	}

	getContentHost() {
		return this.contentHost;
	}

	onChange(listener) {
		this.listeners.push(listener);
		return function() {
			this.listeners = this.listeners.filter(function(entry) {
				return entry !== listener;
			});
		}.bind(this);
	}

	notify() {
		var snapshot = this.getOpenPages();
		var activePageKey = this.activePageKey;
		this.listeners.forEach(function(listener) {
			listener({
				pages: snapshot,
				activePageKey: activePageKey
			});
		});
		window.dispatchEvent(new CustomEvent('workspacepageschange', {
			detail: {
				pages: snapshot,
				activePageKey: activePageKey,
				workspaceName: this.currentWorkspaceName
			}
		}));
	}

	getOpenPages() {
		var pages = [];
		this.pages.forEach(function(entry, pageKey) {
			pages.push({
				pageKey: pageKey,
				title: entry.title,
				url: entry.url,
				mounted: entry.mounted,
				pendingFilters: entry.pendingFilters || null
			});
		});
		return pages;
	}

	getEntry(pageKey) {
		return this.pages.get(pageKey) || null;
	}

	getActiveEntry() {
		if (!this.activePageKey) {
			return null;
		}
		return this.getEntry(this.activePageKey);
	}

	getActiveOverlayHost() {
		var entry = this.getActiveEntry();
		if (entry && entry.overlayHost) {
			return entry.overlayHost;
		}
		return null;
	}

	has(pageKey) {
		return this.pages.has(pageKey);
	}

	register(entry) {
		this.pages.set(entry.pageKey, entry);
		this.notify();
	}

	setActive(pageKey) {
		this.activePageKey = pageKey;
		this.notify();
	}

	remove(pageKey) {
		this.pages.delete(pageKey);
		if (this.activePageKey === pageKey) {
			this.activePageKey = null;
		}
		this.notify();
	}

	clearAll() {
		this.pages.clear();
		this.activePageKey = null;
		this.currentWorkspaceName = 'Default';
		this.currentWorkspaceId = null;
		this.notify();
	}

	setWorkspaceName(name) {
		this.currentWorkspaceName = name || 'Default';
		window.dispatchEvent(new CustomEvent('workspacenamechange', {
			detail: {
				workspaceName: this.currentWorkspaceName,
				workspaceId: this.currentWorkspaceId
			}
		}));
	}

	setWorkspaceIdentity(name, workspaceId) {
		this.currentWorkspaceId = workspaceId || null;
		this.setWorkspaceName(name);
	}

	getWorkspaceName() {
		return this.currentWorkspaceName;
	}

	getWorkspaceId() {
		return this.currentWorkspaceId;
	}

	/**
	 * True when the active session was loaded from or saved as a named save state.
	 */
	hasNamedSaveState() {
		return !!this.currentWorkspaceId
			|| (this.currentWorkspaceName
				&& this.currentWorkspaceName !== 'Default');
	}

	/**
	 * Collect save snapshots from mounted page components that support workspace state.
	 */
	collectSaveState() {
		var pages = [];
		var registry = this;
		this.pages.forEach(function(entry, pageKey) {
			var filters = {};
			if (entry.mounted && entry.paneElement) {
				filters = registry.collectFiltersFromPane(entry.paneElement);
			} else if (entry.pendingFilters) {
				filters = entry.pendingFilters;
			}
			pages.push({
				pageKey: pageKey,
				title: entry.title,
				url: entry.url,
				filters: filters
			});
		});
		return {
			name: this.currentWorkspaceName,
			activePageKey: this.activePageKey,
			pages: pages
		};
	}

	collectFiltersFromPane(paneElement) {
		var capable = findWorkspaceCapableElements(paneElement).filter(function(node) {
			return typeof node.saveWorkspaceState === 'function';
		});
		if (capable.length === 0) {
			return {};
		}
		if (capable.length === 1) {
			return capable[0].saveWorkspaceState() || {};
		}
		var nested = {};
		capable.forEach(function(node) {
			nested[node.localName] = node.saveWorkspaceState() || {};
		});
		return nested;
	}

	findPageRoot(paneElement) {
		if (!paneElement) {
			return null;
		}
		var body = paneElement.querySelector('.page-body');
		if (!body) {
			return null;
		}
		var child = body.firstElementChild;
		while (child) {
			if (child.tagName && child.tagName.indexOf('DL-') === 0) {
				return child;
			}
			child = child.nextElementSibling;
		}
		return body.firstElementChild;
	}
}

export var pageRegistry = new PageRegistry();

if (typeof window !== 'undefined') {
	window.DL = window.DL || {};
	window.DL.pageRegistry = pageRegistry;
}
